import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FiSearch } from 'react-icons/fi';
import NativeDateField from './common/NativeDateField';
import './InventoryLedgerModal.css';

const LABEL_CLOSE_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M17.7072 0.292849C17.3167 -0.0976164 16.6836 -0.0976164 16.2932 0.292849L8.99999 7.58604L1.70683 0.292849C1.31637 -0.0976164 0.683306 -0.0976164 0.29285 0.292849C-0.0976166 0.683305 -0.0976166 1.31637 0.29285 1.70683L7.58602 9.00001L0.29287 16.2931C-0.0975966 16.6836 -0.0975966 17.3166 0.29287 17.7072C0.683326 18.0976 1.31639 18.0976 1.70685 17.7072L8.99999 10.414L16.2932 17.7072C16.6836 18.0976 17.3167 18.0976 17.7072 17.7072C18.0976 17.3166 18.0976 16.6836 17.7072 16.2932L10.414 9.00001L17.7072 1.70683C18.0976 1.31637 18.0976 0.683305 17.7072 0.292849Z" fill="#424143" />
  </svg>
);

const labelClass = 'block text-sm font-normal text-[#424143] mb-1';
const readOnlyClass =
  'w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-gray-50 text-[#2C2C2C] cursor-not-allowed';
const inputClass =
  'w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white text-[#2C2C2C] focus:outline-none focus:ring-1 focus:ring-blue-500';

const EQUIPMENT_LEDGER_FORM_ID = 'equipment-ledger-modal-form';
const PROJECT_SEARCH_RESULT_LIMIT = 20;

const mdyToISO = (mdy) => {
  const slashMatch = String(mdy || '').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  const dashMatch = String(mdy || '').match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashMatch) {
    const [, month, day, year] = dashMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(mdy)) return mdy;
  return '';
};

const isoToMDY = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${y}`;
};

const todayISO = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const getProjectIdFromRecord = (project) =>
  String(project?.Project__r?.Project_System_Wide_ID__c || '').trim();

const getProjectNameFromRecord = (project) =>
  String(project?.Project__r?.Name || '').trim();

const createDefaultItemEntry = () => ({
  runTimeHours: '',
  dailyNotes: '',
});

function ReadOnlyField({ label, value, className = '' }) {
  return (
    <div className={className}>
      <label className={labelClass}>{label}</label>
      <input type="text" readOnly value={value || '—'} className={readOnlyClass} />
    </div>
  );
}

function EquipmentLedgerModal({ isOpen, onClose, editingItem, getAuthConfig, onSaved }) {
  const isEdit = Boolean(editingItem);
  const projectSearchRef = useRef(null);
  const activityDropdownRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const [workDateIso, setWorkDateIso] = useState('');
  const [projectNo, setProjectNo] = useState('');
  const [projectName, setProjectName] = useState('');
  const [branchLocation, setBranchLocation] = useState('');
  const [division, setDivision] = useState('');
  const [activity, setActivity] = useState('');
  const [activities, setActivities] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);

  const [allProjects, setAllProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isActivityDropdownOpen, setIsActivityDropdownOpen] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [equipmentSearchQuery, setEquipmentSearchQuery] = useState('');
  const [existingEquipmentIds, setExistingEquipmentIds] = useState([]);
  const [existingItemsLoading, setExistingItemsLoading] = useState(false);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [isEquipmentPickerOpen, setIsEquipmentPickerOpen] = useState(false);
  const [itemEntryValues, setItemEntryValues] = useState({});

  const [runTimeHours, setRunTimeHours] = useState('');
  const [dailyNotes, setDailyNotes] = useState('');
  const [showRunTimeErrors, setShowRunTimeErrors] = useState(false);

  const equipmentOptions = useMemo(
    () =>
      catalog.filter(
        (item) => item && String(item.description || '').trim() && item.is_active !== false
      ),
    [catalog]
  );

  const availableEquipmentOptions = useMemo(() => {
    const taken = new Set(existingEquipmentIds.map((id) => String(id).toLowerCase()));
    return equipmentOptions.filter(
      (item) => !taken.has(String(item.equipment_id || '').toLowerCase())
    );
  }, [equipmentOptions, existingEquipmentIds]);

  const filteredEquipmentOptions = useMemo(() => {
    const query = equipmentSearchQuery.trim().toLowerCase();
    if (!query) return availableEquipmentOptions;
    return availableEquipmentOptions.filter((item) => {
      const haystack = [item.unit_number, item.description, item.equipment_id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [availableEquipmentOptions, equipmentSearchQuery]);

  const selectedEquipmentItems = useMemo(
    () => equipmentOptions.filter((item) => selectedEquipmentIds.includes(String(item.id))),
    [equipmentOptions, selectedEquipmentIds]
  );

  const resetAddForm = useCallback(() => {
    setWorkDateIso(todayISO());
    setProjectNo('');
    setProjectName('');
    setBranchLocation('');
    setDivision('');
    setActivity('');
    setActivities([]);
    setProjectError(null);
    setCatalogError(null);
    setExistingEquipmentIds([]);
    setSelectedEquipmentIds([]);
    setIsEquipmentPickerOpen(false);
    setEquipmentSearchQuery('');
    setItemEntryValues({});
    setRunTimeHours('');
    setDailyNotes('');
    setShowRunTimeErrors(false);
    setFormError(null);
    setProjectSearchQuery('');
    setIsProjectDropdownOpen(false);
    setIsActivityDropdownOpen(false);
  }, []);

  const loadEditForm = useCallback(() => {
    if (!editingItem) return;
    setWorkDateIso(editingItem.dateIso || mdyToISO(editingItem.date));
    setProjectNo(editingItem.projectNo || '');
    setProjectName(editingItem.projectName || '');
    setBranchLocation(editingItem.branchLocation || '');
    setDivision(editingItem.division || '');
    setActivity(editingItem.activity || '');
    setRunTimeHours(
      editingItem.runTimeHours == null || editingItem.runTimeHours === ''
        ? ''
        : String(editingItem.runTimeHours)
    );
    setDailyNotes(editingItem.dailyNotes || '');
    setShowRunTimeErrors(false);
    setFormError(null);
    setProjectError(null);
  }, [editingItem]);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit) loadEditForm();
    else resetAddForm();
  }, [isOpen, isEdit, loadEditForm, resetAddForm]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const fetchProjectContext = useCallback(async (code) => {
    const trimmed = String(code || '').trim();
    if (!trimmed) {
      setProjectName('');
      setBranchLocation('');
      setActivities([]);
      setActivity('');
      setDivision('');
      return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setProjectError('Backend URL not configured.');
      return;
    }

    setProjectLoading(true);
    setProjectError(null);
    try {
      const response = await axios.get(
        `${baseUrl}/api/miter/production-performance/inventory-ledger/project-context/${encodeURIComponent(trimmed)}`,
        await getAuthConfig()
      );
      if (response.data?.status === 'success' && response.data?.data) {
        const ctx = response.data.data;
        setProjectName(ctx.projectName || '');
        setBranchLocation(ctx.branchLocation || '');
        setActivities(Array.isArray(ctx.activities) ? ctx.activities : []);
        setActivity('');
        setDivision('');
        setSelectedEquipmentIds([]);
        setItemEntryValues({});
        setIsEquipmentPickerOpen(false);
        if (!ctx.activities?.length) {
          setProjectError('No activities found for this project.');
        }
      } else {
        throw new Error(response.data?.message || 'Failed to load project details');
      }
    } catch (err) {
      setProjectName('');
      setBranchLocation('');
      setActivities([]);
      setActivity('');
      setDivision('');
      setProjectError(err.response?.data?.message || err.message || 'Failed to load project details');
    } finally {
      setProjectLoading(false);
    }
  }, [getAuthConfig]);

  const loadAllProjects = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) return;

    const cached = localStorage.getItem('projectsList');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setAllProjects(parsed.filter((project) => project?.Project__r != null));
        }
      } catch {
        // ignore invalid cache
      }
    }

    setProjectsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/salesforce/all-opportunities`,
        await getAuthConfig()
      );
      const records = Array.isArray(response.data?.records) ? response.data.records : [];
      const filtered = records.filter((project) => project?.Project__r != null);
      setAllProjects(filtered);
      localStorage.setItem('projectsList', JSON.stringify(filtered));
    } catch (err) {
      console.error('Failed to load projects for equipment search:', err);
      if (!cached) {
        setProjectError('Failed to load projects. Please try again.');
      }
    } finally {
      setProjectsLoading(false);
    }
  }, [getAuthConfig]);

  useEffect(() => {
    if (!isOpen || isEdit) return;
    loadAllProjects();
  }, [isOpen, isEdit, loadAllProjects]);

  useEffect(() => {
    if (!isOpen || isEdit) return undefined;
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) return undefined;

    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);
    (async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/miter/production-performance/equipment-time-cards`,
          await getAuthConfig()
        );
        if (cancelled) return;
        if (response.data?.status === 'success' && Array.isArray(response.data?.data)) {
          setCatalog(response.data.data);
        } else {
          setCatalog([]);
        }
      } catch (err) {
        if (cancelled) return;
        setCatalog([]);
        setCatalogError(err.response?.data?.message || err.message || 'Failed to load equipment.');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, getAuthConfig]);

  useEffect(() => {
    if (!isOpen || isEdit || !projectNo.trim() || !activity || !workDateIso) {
      if (!isEdit) setExistingEquipmentIds([]);
      return undefined;
    }

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) return undefined;

    let cancelled = false;
    setExistingItemsLoading(true);
    (async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/miter/production-performance/${encodeURIComponent(projectNo.trim())}/equipment-time-card/${workDateIso}`,
          await getAuthConfig({ params: { scope: activity } })
        );
        if (cancelled) return;
        const entries = response.data?.data?.entries;
        const ids = Array.isArray(entries)
          ? entries.map((entry) => String(entry.equipmentId || '').trim()).filter(Boolean)
          : [];
        setExistingEquipmentIds(ids);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load existing equipment for date:', err);
        setExistingEquipmentIds([]);
      } finally {
        if (!cancelled) setExistingItemsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isEdit, projectNo, activity, workDateIso, getAuthConfig]);

  const filteredProjects = useMemo(() => {
    const query = projectSearchQuery.trim().toLowerCase();
    let list = allProjects.filter(
      (project) =>
        getProjectIdFromRecord(project) && project.dv_Division_New__c !== 'Material Sales'
    );
    if (query) {
      list = list.filter((project) => {
        const id = getProjectIdFromRecord(project).toLowerCase();
        const name = getProjectNameFromRecord(project).toLowerCase();
        return id.includes(query) || name.includes(query);
      });
    }
    return list.slice(0, PROJECT_SEARCH_RESULT_LIMIT);
  }, [allProjects, projectSearchQuery]);

  useEffect(() => {
    if (!isProjectDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (projectSearchRef.current && !projectSearchRef.current.contains(event.target)) {
        setIsProjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProjectDropdownOpen]);

  useEffect(() => {
    if (!isActivityDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(event.target)) {
        setIsActivityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isActivityDropdownOpen]);

  const handleSelectProject = (project) => {
    const id = getProjectIdFromRecord(project);
    const name = getProjectNameFromRecord(project);
    if (!id) return;
    setProjectNo(id);
    setProjectName(name);
    setProjectSearchQuery('');
    setIsProjectDropdownOpen(false);
    setSelectedEquipmentIds([]);
    setItemEntryValues({});
    fetchProjectContext(id);
  };

  const handleActivityChange = (value) => {
    setActivity(value);
    const match = activities.find((item) => item.activity === value);
    setDivision(match?.division || '');
    setSelectedEquipmentIds([]);
    setItemEntryValues({});
    setIsEquipmentPickerOpen(false);
    setIsActivityDropdownOpen(false);
  };

  const handleWorkDateChange = (nextIso) => {
    setWorkDateIso(nextIso);
    setSelectedEquipmentIds([]);
    setItemEntryValues({});
    setIsEquipmentPickerOpen(false);
  };

  const toggleEquipmentSelection = (itemId, checked) => {
    const id = String(itemId);
    setSelectedEquipmentIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
    setItemEntryValues((prev) => {
      const next = { ...prev };
      if (checked) next[id] = next[id] || createDefaultItemEntry();
      else delete next[id];
      return next;
    });
  };

  const updateItemEntryValue = (itemId, field, value) => {
    const id = String(itemId);
    setItemEntryValues((prev) => ({
      ...prev,
      [id]: {
        ...createDefaultItemEntry(),
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const getItemEntryValue = (itemId) => ({
    ...createDefaultItemEntry(),
    ...itemEntryValues[String(itemId)],
  });

  const parseHoursInput = (value) => {
    if (value == null || String(value).trim() === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) return undefined;
    return num;
  };

  const isValidRunTime = (value) => typeof parseHoursInput(value) === 'number';

  const runTimeInputClass = (value) =>
    `${inputClass}${
      showRunTimeErrors && !isValidRunTime(value)
        ? ' border-red-500 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    if (!baseUrl) {
      setFormError('Backend URL not configured.');
      return;
    }

    if (isEdit) {
      const hours = parseHoursInput(runTimeHours);
      if (typeof hours !== 'number') {
        setShowRunTimeErrors(true);
        setFormError(
          hours === undefined
            ? 'Run time must be a number greater than or equal to 0.'
            : 'Run Time (Hrs) is required.'
        );
        return;
      }
      setShowRunTimeErrors(false);
      try {
        setSubmitting(true);
        const response = await axios.put(
          `${baseUrl}/api/miter/production-performance/equipment-ledger/${editingItem.id}`,
          {
            runTimeHours: hours,
            dailyNotes: dailyNotes || null,
          },
          await getAuthConfig()
        );
        if (response.data?.status === 'success' || response.status === 200) {
          onSaved?.();
          onClose?.();
        } else {
          throw new Error(response.data?.message || 'Failed to update equipment entry');
        }
      } catch (err) {
        setFormError(err.response?.data?.message || err.message || 'Failed to update equipment entry');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!workDateIso) {
      setFormError('Date is required.');
      return;
    }
    if (!projectNo.trim()) {
      setFormError('Please search and select a project.');
      return;
    }
    if (!activity) {
      setFormError('Activity is required.');
      return;
    }
    if (!selectedEquipmentIds.length) {
      setFormError('Please select at least one equipment item.');
      return;
    }

    for (const equipmentId of selectedEquipmentIds) {
      const entry = getItemEntryValue(equipmentId);
      const hours = parseHoursInput(entry.runTimeHours);
      if (typeof hours !== 'number') {
        setShowRunTimeErrors(true);
        setFormError(
          hours === undefined
            ? 'Run time must be a number greater than or equal to 0.'
            : 'Run Time (Hrs) is required for each selected equipment item.'
        );
        return;
      }
    }
    setShowRunTimeErrors(false);

    try {
      setSubmitting(true);
      const failures = [];
      for (const equipmentMasterId of selectedEquipmentIds) {
        const entry = getItemEntryValue(equipmentMasterId);
        try {
          const response = await axios.post(
            `${baseUrl}/api/miter/production-performance/equipment-ledger`,
            {
              date: isoToMDY(workDateIso),
              projectNo: projectNo.trim(),
              activity,
              equipmentMasterId: Number(equipmentMasterId),
              runTimeHours: parseHoursInput(entry.runTimeHours),
              dailyNotes: entry.dailyNotes || null,
            },
            await getAuthConfig()
          );
          if (response.data?.status !== 'success') {
            failures.push(response.data?.message || `Failed for equipment ${equipmentMasterId}`);
          }
        } catch (err) {
          failures.push(
            err.response?.data?.message || err.message || `Failed for equipment ${equipmentMasterId}`
          );
        }
      }

      if (failures.length === 0) {
        onSaved?.();
        onClose?.();
      } else if (failures.length < selectedEquipmentIds.length) {
        setFormError(`Some equipment was added, but others failed: ${failures.join('; ')}`);
        onSaved?.();
      } else {
        throw new Error(failures.join('; '));
      }
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Failed to create equipment entries');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="inventory-ledger-modal-overlay">
      <div className="inventory-ledger-modal-sheet">
        <div className="inventory-ledger-modal-header">
          <h2 className="inventory-ledger-modal-title">
            {isEdit ? 'Edit Equipment Entry' : 'Add Equipment'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inventory-ledger-modal-close"
            aria-label="Close"
          >
            {LABEL_CLOSE_ICON}
          </button>
        </div>

        <div className="inventory-ledger-modal-body">
          <form id={EQUIPMENT_LEDGER_FORM_ID} onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {formError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isEdit ? (
                <div>
                  <label className={labelClass}>Date</label>
                  <input type="text" readOnly value={isoToMDY(workDateIso)} className={readOnlyClass} />
                </div>
              ) : (
                <NativeDateField
                  label="Date"
                  labelClassName={labelClass}
                  inputClassName={inputClass}
                  placeholder="M/D/YYYY"
                  formatDisplay={isoToMDY}
                  value={workDateIso}
                  onChange={handleWorkDateChange}
                  required
                  className="md:col-span-1"
                />
              )}

              {isEdit ? (
                <ReadOnlyField label="Project ID" value={projectNo} />
              ) : (
                <div className="md:col-span-2" ref={projectSearchRef}>
                  <label className={labelClass}>Search Project</label>
                  <div className="relative">
                    <div className="inventory-ledger-project-search">
                      <FiSearch className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden="true" />
                      <input
                        type="search"
                        placeholder="Search by project ID or name"
                        value={projectSearchQuery}
                        onChange={(e) => {
                          setProjectSearchQuery(e.target.value);
                          setIsProjectDropdownOpen(true);
                        }}
                        onFocus={() => setIsProjectDropdownOpen(true)}
                        className="inventory-ledger-project-search__input w-full"
                        autoComplete="off"
                      />
                    </div>
                    {isProjectDropdownOpen && (
                      <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg inventory-ledger-project-dropdown">
                        {projectsLoading ? (
                          <div className="px-4 py-3 text-sm text-gray-500">Loading projects…</div>
                        ) : filteredProjects.length > 0 ? (
                          filteredProjects.map((project) => {
                            const id = getProjectIdFromRecord(project);
                            const name = getProjectNameFromRecord(project);
                            return (
                              <button
                                key={project.Id || id}
                                type="button"
                                onClick={() => handleSelectProject(project)}
                                className="w-full px-4 py-2.5 text-left text-sm hover:bg-red-50 border-b border-gray-100 last:border-b-0"
                              >
                                <span className="font-medium text-gray-900">{id}</span>
                                {name && <span className="text-gray-600"> — {name}</span>}
                              </button>
                            );
                          })
                        ) : (
                          <div className="px-4 py-3 text-sm text-gray-500">
                            {projectSearchQuery.trim()
                              ? 'No projects match your search.'
                              : 'Type a project ID or name to search.'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {projectNo && (
                    <p className="mt-1 text-xs text-gray-600">
                      Selected: <span className="font-medium">{projectNo}</span>
                      {projectName ? ` — ${projectName}` : ''}
                    </p>
                  )}
                  {projectLoading && (
                    <p className="mt-1 text-xs text-gray-500">Loading project details…</p>
                  )}
                  {projectError && <p className="mt-1 text-xs text-amber-700">{projectError}</p>}
                </div>
              )}

              <ReadOnlyField label="Project Name" value={projectName} />
              <ReadOnlyField label="Branch (Location)" value={branchLocation} />

              {isEdit ? (
                <>
                  <ReadOnlyField label="Activity" value={activity} />
                  <ReadOnlyField label="Division" value={division} />
                  <ReadOnlyField label="Unit Number" value={editingItem?.unitNumber} />
                  <ReadOnlyField label="Equipment ID" value={editingItem?.equipmentId} />
                  <ReadOnlyField
                    label="Description"
                    value={editingItem?.description}
                    className="md:col-span-2"
                  />
                </>
              ) : (
                <div className="md:col-span-2" ref={activityDropdownRef}>
                  <label className={labelClass}>Activity</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        if (!activities.length || projectLoading) return;
                        setIsEquipmentPickerOpen(false);
                        setIsActivityDropdownOpen((open) => !open);
                      }}
                      disabled={!activities.length || projectLoading}
                      className={`${inputClass} h-10 text-left flex items-center justify-between gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                      aria-haspopup="listbox"
                      aria-expanded={isActivityDropdownOpen}
                    >
                      <span className={activity ? 'text-gray-900' : 'text-gray-500'}>
                        {activity || 'Select activity…'}
                      </span>
                      <svg
                        className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isActivityDropdownOpen ? 'rotate-180' : ''}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {isActivityDropdownOpen && (
                      <div
                        className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                        role="listbox"
                      >
                        {activities.map((item) => {
                          const isSelected = activity === item.activity;
                          return (
                            <button
                              key={item.activity}
                              type="button"
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => handleActivityChange(item.activity)}
                              className={`w-full px-4 py-2.5 text-left text-sm border-b border-gray-100 last:border-b-0 ${
                                isSelected ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-900 hover:bg-gray-50'
                              }`}
                            >
                              {item.activity}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {activity && <p className="mt-1 text-xs text-gray-500">Division: {division || '—'}</p>}
                </div>
              )}

              {!isEdit && (
                <div className="md:col-span-2">
                  <label className={labelClass}>Equipment</label>
                  <button
                    type="button"
                    onClick={() => {
                      setIsActivityDropdownOpen(false);
                      setIsEquipmentPickerOpen((open) => !open);
                    }}
                    disabled={!activity || catalogLoading || existingItemsLoading}
                    className="w-full h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm text-left text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {catalogLoading || existingItemsLoading
                      ? 'Loading equipment…'
                      : selectedEquipmentIds.length > 0
                        ? `${selectedEquipmentIds.length} item(s) selected`
                        : 'Select equipment…'}
                  </button>

                  {isEquipmentPickerOpen && (
                    <div className="mt-2 rounded-lg border border-gray-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                        <h3 className="text-sm font-semibold text-gray-900">Add Equipment</h3>
                        <button
                          type="button"
                          onClick={() => setIsEquipmentPickerOpen(false)}
                          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                          aria-label="Close"
                        >
                          &times;
                        </button>
                      </div>
                      <div className="px-4 py-3 max-h-[min(50vh,28rem)] overflow-y-auto">
                        {catalogLoading || existingItemsLoading ? (
                          <p className="text-sm text-gray-500">Loading equipment…</p>
                        ) : catalogError ? (
                          <p className="text-sm text-red-600">{catalogError}</p>
                        ) : equipmentOptions.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No equipment uploaded yet. Upload the equipment catalog from configuration.
                          </p>
                        ) : availableEquipmentOptions.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            All equipment is already added for this project, activity, and date.
                          </p>
                        ) : (
                          <>
                            <input
                              type="search"
                              value={equipmentSearchQuery}
                              onChange={(e) => setEquipmentSearchQuery(e.target.value)}
                              placeholder="Search unit number or description…"
                              className={`${inputClass} mb-3`}
                            />
                            {filteredEquipmentOptions.length === 0 ? (
                              <p className="text-sm text-gray-500">No equipment matches that search.</p>
                            ) : (
                              <div className="space-y-2">
                                {filteredEquipmentOptions.map((item) => {
                                  const itemId = String(item.id);
                                  return (
                                    <label key={itemId} className="flex gap-2 items-start cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={selectedEquipmentIds.includes(itemId)}
                                        onChange={(e) => toggleEquipmentSelection(itemId, e.target.checked)}
                                        className="mt-0.5 rounded border-gray-300"
                                      />
                                      <span className="text-sm text-gray-800">
                                        <span className="font-medium">{item.unit_number || item.equipment_id}</span>
                                        {' — '}
                                        {item.description}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                        <button
                          type="button"
                          onClick={() => setIsEquipmentPickerOpen(false)}
                          className="w-full h-9 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                        >
                          Done ({selectedEquipmentIds.length} selected)
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedEquipmentItems.length > 0 && (
                    <div className="mt-4 space-y-4">
                      {selectedEquipmentItems.map((item) => {
                        const entry = getItemEntryValue(item.id);
                        return (
                          <div key={item.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div className="mb-3">
                              <p className="text-sm font-semibold text-gray-900">
                                {item.unit_number || item.equipment_id} — {item.description}
                              </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className={labelClass}>
                                  Run Time (Hrs)
                                  <span className="text-red-500"> *</span>
                                </label>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={entry.runTimeHours}
                                  onChange={(e) =>
                                    updateItemEntryValue(item.id, 'runTimeHours', e.target.value)
                                  }
                                  placeholder="0"
                                  required
                                  aria-required="true"
                                  aria-invalid={
                                    showRunTimeErrors && !isValidRunTime(entry.runTimeHours)
                                      ? true
                                      : undefined
                                  }
                                  className={runTimeInputClass(entry.runTimeHours)}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <label className={labelClass}>Daily Notes</label>
                                <textarea
                                  rows={2}
                                  value={entry.dailyNotes}
                                  onChange={(e) =>
                                    updateItemEntryValue(item.id, 'dailyNotes', e.target.value)
                                  }
                                  placeholder="Optional notes"
                                  className={inputClass}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {isEdit && (
                <>
                  <div>
                    <label className={labelClass}>
                      Run Time (Hrs)
                      <span className="text-red-500"> *</span>
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={runTimeHours}
                      onChange={(e) => setRunTimeHours(e.target.value)}
                      placeholder="0"
                      required
                      aria-required="true"
                      aria-invalid={
                        showRunTimeErrors && !isValidRunTime(runTimeHours) ? true : undefined
                      }
                      className={runTimeInputClass(runTimeHours)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Daily Notes</label>
                    <textarea
                      rows={2}
                      value={dailyNotes}
                      onChange={(e) => setDailyNotes(e.target.value)}
                      placeholder="Optional notes"
                      className={inputClass}
                    />
                  </div>
                </>
              )}
            </div>
          </form>
        </div>

        <div className="inventory-ledger-modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inventory-ledger-modal-btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={EQUIPMENT_LEDGER_FORM_ID}
            disabled={submitting || (!isEdit && (!projectNo || !activity || selectedEquipmentIds.length === 0))}
            className="inventory-ledger-modal-btn-primary"
          >
            {submitting
              ? 'Saving…'
              : isEdit
                ? 'Save Entry'
                : selectedEquipmentIds.length > 1
                  ? `Add ${selectedEquipmentIds.length} Equipment`
                  : 'Add Equipment'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default EquipmentLedgerModal;
